// PLN-02 (Job-contact.dc.html "calendar"): interviews that overlap in time stand side by side,
// as a calendar draws them — any overlap, not only the same start. Times are minutes into the day.

export interface TimeSpan {
  readonly id: string;
  readonly start: number;
  /** Exclusive: one that ends at 11:00 and one that starts at 11:00 don't overlap. */
  readonly end: number;
}

export interface ColumnPlacement {
  /** Which column, from 0 at the left. */
  readonly column: number;
  /** How many columns its group of overlapping interviews shares the day's width between. */
  readonly columns: number;
}

export const FULL_WIDTH: ColumnPlacement = { column: 0, columns: 1 };

/**
 * Each interview takes the leftmost column free when it starts. A group is every interview joined
 * to another by an overlap, directly or through a chain; the whole group shares one column count,
 * so the blocks in it line up.
 */
export function layoutOverlaps(spans: readonly TimeSpan[]): ReadonlyMap<string, ColumnPlacement> {
  const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end || a.id.localeCompare(b.id));
  const placements = new Map<string, ColumnPlacement>();
  let group: { readonly id: string; readonly column: number }[] = [];
  let columnEnds: number[] = [];
  let groupEnd = Number.NEGATIVE_INFINITY;

  const closeGroup = (): void => {
    for (const member of group) placements.set(member.id, { column: member.column, columns: columnEnds.length });
    group = [];
    columnEnds = [];
  };

  for (const span of sorted) {
    if (group.length > 0 && span.start >= groupEnd) closeGroup();
    const free = columnEnds.findIndex((end) => end <= span.start);
    const column = free === -1 ? columnEnds.length : free;
    columnEnds[column] = span.end;
    group.push({ id: span.id, column });
    groupEnd = group.length === 1 ? span.end : Math.max(groupEnd, span.end);
  }
  closeGroup();
  return placements;
}
